import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneRight, Sparkle, GitBranch, Plus, XCircle, Info, ChatsCircle, Trash, Lightbulb, Warning } from "@phosphor-icons/react";

const WINDOWS = [
    { value: "5y", label: "Solo recientes (≤5 años)" },
    { value: "10y", label: "Últimos 10 años" },
    { value: "all", label: "Toda la evidencia" },
];

const QUICK = [
    "Reanaliza la fórmula completa con espíritu crítico: ¿qué componente aporta menos y por qué?",
    "¿Qué cambio mejoraría la fórmula según la evidencia más reciente?",
    "Propón una variante más económica sin perder eficacia documentada",
];

const QUICK_CREATIVE = [
    "Propón una variante exploratoria y diferencial apoyándote en ensayos en curso",
    "¿Qué compuesto emergente con lógica fisiológica coherente podría hacer esta fórmula única?",
    "Diseña la versión más innovadora posible de esta fórmula manteniendo la seguridad",
];

export default function FormulaChat({ formula }) {
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [win, setWin] = useState("all");
    const [mode, setMode] = useState("rigorous");
    const [force, setForce] = useState(false);
    const [busy, setBusy] = useState(false);
    const [applying, setApplying] = useState(null);
    const bottomRef = useRef(null);
    const pollRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get(`/formulas/${formula.id}/chat`);
            setMessages(data.messages || []);
            return data.messages || [];
        } catch { return []; }
    };

    const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    const startPoll = () => {
        stopPoll();
        let tries = 0;
        pollRef.current = setInterval(async () => {
            tries += 1;
            const msgs = await load();
            const pending = msgs.some((m) => m.status === "thinking");
            if (!pending || tries > 45) { stopPoll(); setBusy(false); }
        }, 4000);
    };

    useEffect(() => {
        load().then((msgs) => { if (msgs.some((m) => m.status === "thinking")) { setBusy(true); startPoll(); } });
        return stopPoll;
        // eslint-disable-next-line
    }, [formula.id]);

    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

    const send = async (text, opts = {}) => {
        const message = (text ?? input).trim();
        if (!message || busy) return;
        if (text === undefined) setInput("");
        setBusy(true);
        try {
            await api.post(`/formulas/${formula.id}/chat`, {
                message, evidence_window: win, mode,
                force: opts.force || force,
            });
            await load();
            startPoll();
        } catch (e) {
            setBusy(false);
            toast.error(e.response?.data?.detail || "Error enviando el mensaje");
        }
    };

    // Reenvía la petición original del usuario en modo forzado (incluir aunque no sea óptimo)
    const forceInclude = (assistantMsg) => {
        const idx = messages.findIndex((x) => x.id === assistantMsg.id);
        const userMsg = idx > 0 ? messages[idx - 1] : null;
        const original = userMsg?.content || "";
        send(`FORZAR INCLUSIÓN (incluir aunque no sea óptimo, a la dosis más segura según evidencia): ${original}`,
             { force: true });
        toast.info("Forzando la inclusión a la dosis más segura…");
    };

    const apply = async (msgId, asNew) => {
        setApplying(msgId + (asNew ? "-new" : "-v"));
        try {
            const { data } = await api.post(`/formulas/${formula.id}/chat/${msgId}/apply`, { as_new_formula: asNew });
            toast.success(asNew ? "Fórmula nueva creada a partir de la propuesta" : `Nueva versión v${data.version} creada`);
            navigate(`/formulas/${data.id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo aplicar la propuesta");
        } finally { setApplying(null); }
    };

    const clearChat = async () => {
        await api.delete(`/formulas/${formula.id}/chat`);
        setMessages([]);
        toast.success("Conversación borrada");
    };

    return (
        <div className="max-w-3xl" data-testid="formula-chat">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <p className="text-sm text-[var(--ink-2)] max-w-lg">
                    {mode === "creative" ? (
                        <>Modo <strong>creativo</strong>: la IA explora variantes diferenciales apoyándose en
                        ensayos en curso y evidencia emergente — siempre dentro de la seguridad y los límites europeos.</>
                    ) : (
                        <>Dialoga con la fórmula: pregunta, pide añadir/quitar ingredientes o un reanálisis completo.
                        La IA es <strong>crítica y nada concesiva</strong> — si tu propuesta no se sostiene en la evidencia, la rechazará.</>
                    )}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex border hairline" data-testid="chat-mode-toggle">
                        <button onClick={() => setMode("rigorous")}
                            className={`px-3 py-2 text-xs transition-colors ${mode === "rigorous" ? "bg-[var(--ink)] text-white" : "bg-white hover:bg-[var(--cream)]"}`}
                            data-testid="chat-mode-rigorous">
                            Riguroso
                        </button>
                        <button onClick={() => setMode("creative")}
                            className={`px-3 py-2 text-xs transition-colors inline-flex items-center gap-1 ${mode === "creative" ? "bg-[var(--terra-2)] text-white" : "bg-white hover:bg-[var(--cream)]"}`}
                            data-testid="chat-mode-creative">
                            <Lightbulb size={12}/> Creativo
                        </button>
                    </div>
                    <select
                        value={win}
                        onChange={(e) => setWin(e.target.value)}
                        className="border hairline bg-white text-xs px-2 py-2 outline-none"
                        data-testid="chat-evidence-window-select"
                    >
                        {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                    </select>
                    {messages.length > 0 && (
                        <button onClick={clearChat} title="Borrar conversación" className="p-2 border hairline bg-white hover:bg-[var(--cream)] text-[var(--ink-3)]" data-testid="chat-clear-button">
                            <Trash size={14}/>
                        </button>
                    )}
                </div>
            </div>

            {messages.length === 0 && (
                <div className="border hairline bg-white p-6 mb-4" data-testid="chat-empty-state">
                    <div className="flex items-center gap-2 label-eyebrow mb-3"><ChatsCircle size={14}/> Empieza el diálogo</div>
                    <div className="flex flex-col gap-2">
                        {(mode === "creative" ? QUICK_CREATIVE : QUICK).map((q, i) => (
                            <button key={i} onClick={() => send(q)} disabled={busy}
                                className="text-left text-sm border hairline px-3 py-2.5 bg-[var(--cream)] hover:bg-white transition-colors disabled:opacity-50"
                                data-testid={`chat-quick-${i}`}>
                                {q}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="space-y-4 mb-4">
                {messages.map((m) => <Message key={m.id} m={m} apply={apply} applying={applying} forceInclude={forceInclude} />)}
                <div ref={bottomRef} />
            </div>

            <div className="sticky bottom-4">
                <div className="flex items-center gap-2 mb-2">
                    <button onClick={() => setForce((f) => !f)}
                        className={`text-xs px-2.5 py-1.5 border inline-flex items-center gap-1.5 transition-colors ${force ? "border-[var(--terra-2)] bg-[var(--terra-2)] text-white" : "hairline bg-white hover:bg-[var(--cream)] text-[var(--ink-2)]"}`}
                        data-testid="chat-force-toggle">
                        <Warning size={12}/> Forzar inclusión {force ? "· ACTIVO" : ""}
                    </button>
                    {force && (
                        <span className="text-[11px] text-[var(--terra-2)]">
                            Incluirá el ingrediente aunque no sea óptimo, a dosis segura, con su grado riesgo/beneficio.
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        rows={2}
                        placeholder={busy ? "La IA está analizando…" : force ? "Ej.: incluye creatina en la fórmula aunque no sea óptima…" : "Ej.: añade zinc a la fórmula · quita el omega-3 · reanaliza con estudios recientes…"}
                        className={`flex-1 border bg-white text-sm px-3 py-2.5 outline-none resize-none focus:border-[var(--sage)] ${force ? "border-[var(--terra-2)]" : "hairline"}`}
                        disabled={busy}
                        data-testid="chat-input"
                    />
                    <button onClick={() => send()} disabled={busy || !input.trim()}
                        className="btn-primary px-4 disabled:opacity-40 inline-flex items-center gap-2"
                        data-testid="chat-send-button">
                        {busy ? <Sparkle size={16} className="animate-pulse"/> : <PaperPlaneRight size={16}/>}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Message({ m, apply, applying, forceInclude }) {
    if (m.role === "user") {
        return (
            <div className="flex justify-end" data-testid={`chat-msg-user-${m.id}`}>
                <div className="bg-[var(--ink)] text-[var(--bone)] px-4 py-3 text-sm max-w-[85%] whitespace-pre-wrap">{m.content}</div>
            </div>
        );
    }
    if (m.status === "thinking") {
        return (
            <div className="border hairline bg-white p-4 flex items-center gap-3 max-w-[85%]" data-testid="chat-msg-thinking">
                <Sparkle size={18} className="text-[var(--sage)] animate-pulse" weight="duotone"/>
                <span className="text-sm text-[var(--ink-2)]">Analizando contra la evidencia… (30-90 s)</span>
            </div>
        );
    }
    if (m.status === "error") {
        return (
            <div className="border hairline bg-[var(--cream)] p-4 max-w-[85%] text-sm text-[var(--terra-2)]" data-testid="chat-msg-error">
                <strong>Error:</strong> {m.content}
            </div>
        );
    }
    const verdictBadge = {
        proposal: <span className="citation-badge bg-[var(--sage)] text-white border-[var(--sage)] inline-flex items-center gap-1"><Plus size={11}/> PROPUESTA DE CAMBIO</span>,
        rejected: <span className="citation-badge bg-[var(--terra-2)] text-white border-[var(--terra-2)] inline-flex items-center gap-1"><XCircle size={11}/> RECHAZADO — sin respaldo</span>,
        info: <span className="citation-badge inline-flex items-center gap-1"><Info size={11}/> Información</span>,
    }[m.verdict || "info"];

    return (
        <div className="border hairline bg-white p-5 max-w-[92%] space-y-3" data-testid={`chat-msg-assistant-${m.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
                {verdictBadge}
                {m.mode === "creative" && (
                    <span className="citation-badge bg-[var(--terra-2)]/10 text-[var(--terra-2)] border-[var(--terra-2)] inline-flex items-center gap-1" data-testid={`chat-creative-badge-${m.id}`}>
                        <Lightbulb size={11}/> MODO CREATIVO
                    </span>
                )}
            </div>
            <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.content}</div>
            {m.verdict === "rejected" && (m.rejection_reasons || []).length > 0 && (
                <ul className="text-sm space-y-1 border-l-2 border-[var(--terra-2)] pl-3 text-[var(--ink-2)]">
                    {m.rejection_reasons.map((r, i) => <li key={i}>• {r}</li>)}
                </ul>
            )}
            {m.verdict === "rejected" && m.rejected_assessment && (
                <div data-testid={`chat-rejected-assessment-${m.id}`}>
                    <div className="flex items-center justify-between gap-2 text-sm border border-[var(--terra-2)] bg-[#FBEAE9] px-3 py-2">
                        <span className="truncate font-medium">{m.rejected_assessment.compound_name}</span>
                        {m.rejected_assessment.safe_dose?.dose_mg && (
                            <span className="mono text-xs text-[var(--ink-2)] flex-shrink-0" data-testid={`chat-rejected-safe-dose-${m.id}`}>
                                dosis segura: {m.rejected_assessment.safe_dose.dose_mg} {m.rejected_assessment.safe_dose.unit || "mg"}
                                {m.rejected_assessment.safe_dose.frequency ? ` · ${m.rejected_assessment.safe_dose.frequency}` : ""}
                            </span>
                        )}
                    </div>
                    {m.rejected_assessment.risk_benefit && (
                        <RiskBenefit rb={m.rejected_assessment.risk_benefit} forced testid={`chat-rejected-risk-benefit-${m.id}`}/>
                    )}
                    {m.rejected_assessment.safe_dose?.note && (
                        <p className="text-[11px] text-[var(--ink-3)] mt-1.5">{m.rejected_assessment.safe_dose.note}</p>
                    )}
                </div>
            )}
            {m.verdict === "rejected" && (
                <div className="border-l-2 border-[var(--terra-2)] pl-3">
                    <p className="text-xs text-[var(--ink-2)] mb-2">
                        ¿Aun así quieres incluirlo? Forzaremos la inclusión a la dosis más segura según la evidencia
                        (sin superar el límite europeo), marcándolo en <span className="text-[var(--terra-2)] font-medium">rojo</span> como no óptimo.
                    </p>
                    <button onClick={() => forceInclude(m)} disabled={!!applying}
                        className="px-3 py-2 border border-[var(--terra-2)] text-[var(--terra-2)] bg-white text-xs hover:bg-[var(--terra-2)] hover:text-white transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                        data-testid={`chat-force-include-${m.id}`}>
                        <Warning size={13}/> Forzar inclusión de todas formas
                    </button>
                </div>
            )}
            {m.proposal && (
                <div className="border hairline bg-[var(--cream)] p-4 space-y-3" data-testid={`chat-proposal-${m.id}`}>
                    <div className="label-eyebrow">Fórmula recalculada propuesta</div>
                    {m.proposal.change_summary && <p className="text-sm font-medium">{m.proposal.change_summary}</p>}
                    <div className="space-y-1.5">
                        {(m.proposal.components || []).map((c, i) => {
                            const forced = c.inclusion_type === "forced";
                            const improvement = c.inclusion_type === "improvement";
                            const rowCls = forced
                                ? "bg-[#FBEAE9] border-[var(--terra-2)]"
                                : improvement
                                ? "bg-[#E8F1E9] border-[var(--sage)]"
                                : "bg-white hairline";
                            return (
                                <div key={i} data-testid={`chat-proposal-component-${i}`}>
                                    <div className={`flex items-center justify-between gap-2 text-sm border px-3 py-2 ${rowCls}`}>
                                        <span className="flex items-center gap-2 min-w-0 flex-wrap">
                                            <span className="truncate">{c.compound_name}</span>
                                            {forced && (
                                                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[var(--terra-2)] text-white flex-shrink-0 inline-flex items-center gap-1" data-testid={`chat-component-forced-${i}`}>
                                                    <Warning size={9}/> forzado · no óptimo
                                                </span>
                                            )}
                                            {improvement && (
                                                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[var(--sage)] text-white flex-shrink-0 inline-flex items-center gap-1" data-testid={`chat-component-improvement-${i}`}>
                                                    <Plus size={9}/> mejora validada
                                                </span>
                                            )}
                                            {!forced && !improvement && c.evidence_level && c.evidence_level !== "established" && (
                                                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border flex-shrink-0 ${c.evidence_level === "exploratory" ? "border-[var(--terra-2)] text-[var(--terra-2)]" : "border-[var(--sage)] text-[var(--sage)]"}`}>
                                                    {c.evidence_level === "exploratory" ? "exploratorio" : "emergente"}
                                                </span>
                                            )}
                                        </span>
                                        <span className="mono text-xs text-[var(--ink-2)] flex-shrink-0">{c.dose_mg} {c.unit || "mg"} · {c.frequency}</span>
                                    </div>
                                    {c.risk_benefit && (forced || improvement) && (
                                        <RiskBenefit rb={c.risk_benefit} forced={forced} testid={`chat-risk-benefit-${i}`}/>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {m.applied_formula_id ? (
                        <div className="text-xs text-[var(--sage)] font-medium" data-testid={`chat-proposal-applied-${m.id}`}>✓ Propuesta ya aplicada</div>
                    ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                            <button onClick={() => apply(m.id, false)} disabled={!!applying}
                                className="btn-primary text-xs px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
                                data-testid={`chat-apply-version-${m.id}`}>
                                <GitBranch size={13}/> {applying === m.id + "-v" ? "Aplicando…" : "Aplicar como nueva versión"}
                            </button>
                            <button onClick={() => apply(m.id, true)} disabled={!!applying}
                                className="px-3 py-2 border hairline bg-white text-xs hover:bg-white/60 inline-flex items-center gap-1.5 disabled:opacity-50"
                                data-testid={`chat-apply-new-${m.id}`}>
                                <Plus size={13}/> {applying === m.id + "-new" ? "Creando…" : "Crear fórmula nueva"}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function RiskBenefit({ rb, forced, testid }) {
    const grade = Math.max(1, Math.min(10, Number(rb.grade) || 0));
    const color = grade >= 7 ? "var(--sage)" : grade >= 4 ? "#B8860B" : "var(--terra-2)";
    return (
        <div className={`border border-t-0 px-3 py-2 text-xs space-y-1.5 ${forced ? "border-[var(--terra-2)] bg-white" : "border-[var(--sage)] bg-white"}`} data-testid={testid}>
            <div className="flex items-center gap-3 flex-wrap">
                <span className="uppercase tracking-wider text-[10px] text-[var(--ink-3)]">Riesgo/Beneficio</span>
                <span className="mono font-medium" style={{ color }}>{grade}/10</span>
                <div className="w-24 h-1.5 bg-[var(--cream)] border hairline">
                    <div className="h-full" style={{ width: `${grade * 10}%`, backgroundColor: color }}/>
                </div>
                {rb.risk_level && <span className="text-[var(--ink-2)]">riesgo: <strong>{rb.risk_level}</strong></span>}
                {rb.benefit_level && <span className="text-[var(--ink-2)]">beneficio: <strong>{rb.benefit_level}</strong></span>}
            </div>
            {rb.justification && <p className="text-[var(--ink-2)] leading-snug">{rb.justification}</p>}
        </div>
    );
}
